/**
 * Composition Root for UI
 *
 * This is the single place where business logic (store) and UI components are wired together.
 * Views receive their dependencies through factory functions, enabling easy testing.
 *
 * Domain-specific wiring is delegated to modules in ./compositionRoot/.
 */

import type { RouteObject } from "react-router-dom";

import type { TAppStore } from "@/store";
import { createUseAppStore } from "@/store";

import { createApiClient, type TApiClient } from "@/apis/proda_finance";

import { wireHomepage } from "./compositionRoot/wireHomepage";
import { wireFiles } from "./compositionRoot/wireFiles";
import { wireUpload } from "./compositionRoot/wireUpload";
import { wireDeals } from "./compositionRoot/wireDeals";
import { wireAdmin } from "./compositionRoot/wireAdmin";
import { wireBusinessPartners } from "./compositionRoot/wireBusinessPartners";
import { wireAuditLog } from "./compositionRoot/wireAuditLog";
import { wireEmailCommunications } from "./compositionRoot/wireEmailCommunications";
import { wireFileReview } from "./compositionRoot/wireFileReview";
import { wireGLLines } from "./compositionRoot/wireGLLines";
import { wireSupport } from "./compositionRoot/wireSupport";
import { wireSettings } from "./compositionRoot/wireSettings";
import { wireDealDetail } from "./compositionRoot/wireDealDetail";
import { wireRootLayout } from "./compositionRoot/wireRootLayout";
import { wireServiceProviderCoAMappings } from "./compositionRoot/wireServiceProviderCoAMappings";

type TCompositionRootConfig = {
    store: TAppStore;
    apiBaseUrl?: string;
};

export const createCompositionRootUI = ({ store, apiBaseUrl = "/api" }: TCompositionRootConfig) => {
    const useAppStore = createUseAppStore(store);

    const api: TApiClient = createApiClient({
        baseUrl: apiBaseUrl,
        requestInterceptors: [
            async (request) => {
                const state = store.getState();
                const companyId = state.effectiveCompanyId || state.user?.companyId;

                const { tokenService } = await import("@/auth/tokenService");
                const accessToken = await tokenService.getAccessToken();

                const headers: Record<string, string> = {
                    ...(request.headers as Record<string, string>),
                };

                if (companyId) {
                    headers["X-Company-ID"] = companyId;
                }

                const effectiveSubgroup = state.effectiveSubgroup;
                if (effectiveSubgroup) {
                    headers["X-Effective-Subgroup"] = effectiveSubgroup;
                }

                if (accessToken) {
                    headers["Authorization"] = `Bearer ${accessToken}`;
                }

                return {
                    ...request,
                    headers,
                };
            },
        ],
    });

    const deps = { store, useAppStore, api };

    const { ViewHomepage } = wireHomepage(deps);
    const { ViewFiles, ViewFilesTBGL, ViewFilesFinJournals, ViewFilesArchive } = wireFiles(deps);
    const { ViewUploadFinJournals, ViewUploadStandaloneFinJournals, ViewUploadTBGL } =
        wireUpload(deps);
    const { ViewDeals, ViewDealCreate } = wireDeals(deps);
    const { ViewCompanies, ViewUsers, ViewAnnouncements } = wireAdmin(deps);
    const { ViewServiceProviders, ViewBusinessPartnerDeals, ViewBusinessPartnerDetails } =
        wireBusinessPartners(deps);
    const { ViewAuditLog, ViewGlobalAuditLog } = wireAuditLog(deps);
    const { ViewEmailCommunications } = wireEmailCommunications(deps);
    const { ViewFileReview } = wireFileReview(deps);
    const { GLLinesRedirect } = wireGLLines(deps);
    const { ViewSupportTickets, ViewSupportTicketDetail, ViewForwardedTickets } = wireSupport(deps);
    const {
        ViewSettings,
        ViewChartOfAccounts,
        ViewPropertyCodes,
        ViewBooks,
        ViewSegmentValues,
        ViewCompanySettings,
        ViewYardiImport,
    } = wireSettings(deps);
    const { ViewDealDetail } = wireDealDetail(deps);
    const { ViewServiceProviderCoAMappings } = wireServiceProviderCoAMappings(deps);
    const {
        RootLayout,
        LandingRedirect,
        adminOnlyRoutes: _adminOnlyRoutes,
        prodaOnlyRoutes: _prodaOnlyRoutes,
    } = wireRootLayout(deps, ViewSettings);

    const routes: RouteObject[] = [
        {
            path: "/",
            element: <RootLayout />,
            children: [
                // Where "/" lands depends on the caller's permissions and the
                // Homepage v2 flag, so it is resolved at render time rather
                // than hardcoded here.
                { index: true, element: <LandingRedirect /> },
                { path: "home", element: <ViewHomepage /> },
                {
                    path: "upload/fin-journals-standalone",
                    element: <ViewUploadStandaloneFinJournals />,
                },
                { path: "upload/tb-gl", element: <ViewUploadTBGL /> },
                // The legacy Files and Financial TB/GL lists stay until the
                // Homepage v2 board is enabled everywhere (the
                // `homepage_v2_enabled` company flag). They are what a company
                // without the flag lands on; they are removed once the board
                // is on in production.
                { path: "files", element: <ViewFiles /> },
                { path: "files/tb-gl", element: <ViewFilesTBGL /> },
                { path: "files/fin-journals", element: <ViewFilesFinJournals /> },
                { path: "files/archive", element: <ViewFilesArchive /> },
                { path: "files/:fileId", element: <ViewFileReview /> },
                { path: "files/:fileId/gl-lines", element: <GLLinesRedirect /> },
                { path: "support", element: <ViewSupportTickets /> },
                { path: "support/:ticketId", element: <ViewSupportTicketDetail /> },
                { path: "admin/audit-log", element: <ViewAuditLog /> },
                { path: "admin/email-communications", element: <ViewEmailCommunications /> },
                { path: "admin/global-audit-log", element: <ViewGlobalAuditLog /> },
                { path: "admin/companies", element: <ViewCompanies /> },
                { path: "admin/support-forwarded", element: <ViewForwardedTickets /> },
                { path: "admin/users", element: <ViewUsers /> },
                { path: "admin/business-partners", element: <ViewServiceProviders /> },
                {
                    path: "admin/business-partners/:businessPartnerId/details",
                    element: <ViewBusinessPartnerDetails />,
                },
                {
                    path: "admin/business-partners/:serviceProviderId/coa",
                    element: <ViewServiceProviderCoAMappings />,
                },
                {
                    path: "admin/business-partners/:bpId/deals",
                    element: <ViewBusinessPartnerDeals />,
                },
                { path: "admin/business-partners/:bpId/deals/new", element: <ViewDealCreate /> },
                {
                    path: "admin/business-partners/:bpId/deals/:dealId",
                    element: <ViewDealDetail />,
                },
                { path: "admin/portfolio-companies/:pcId/deals/new", element: <ViewDealCreate /> },
                { path: "admin/announcements", element: <ViewAnnouncements /> },
                { path: "settings/chart-of-accounts", element: <ViewChartOfAccounts /> },
                { path: "settings/books", element: <ViewBooks /> },
                { path: "admin/submissions", element: <ViewCompanySettings /> },
                { path: "settings/propcodes", element: <ViewPropertyCodes /> },
                { path: "settings/segments", element: <ViewSegmentValues /> },
                { path: "settings/yardi-import", element: <ViewYardiImport /> },
            ],
        },
    ];

    return {
        routes,
        ViewFiles,
        ViewFilesTBGL,
        ViewUploadFinJournals,
        ViewUploadTBGL,
        ViewFilesFinJournals,
        ViewFilesArchive,
        ViewFileReview,
        ViewDeals,
        ViewDealCreate,
        ViewSettings,
        ViewAuditLog,
        ViewEmailCommunications,
        ViewGlobalAuditLog,
        ViewCompanies,
        ViewUsers,
        ViewServiceProviders,
        ViewAnnouncements,
        ViewSupportTickets,
        ViewSupportTicketDetail,
        ViewForwardedTickets,
        ViewBusinessPartnerDeals,
        ViewDealDetail,
        ViewChartOfAccounts,
        ViewPropertyCodes,
        ViewSegmentValues,
        ViewCompanySettings,
        ViewYardiImport,
    };
};
